import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Context } from "app/model/dictionary/dictionaryContextInterface.model";
import { FormFieldOption } from "app/shared/components/form-fields/base-input/base-input.component";
import { BehaviorSubject, Observable, tap } from "rxjs";
import { IDrillDownItem } from "../modules/admin/analytics/interfaces/analytics.types";

@Injectable({
    providedIn: "root",
})
export class DictionaryService {
    private _dictionary: BehaviorSubject<any> = new BehaviorSubject({});
    constructor(private _httpClient: HttpClient) {}

    get dictionary$(): Observable<any> {
        return this._dictionary.asObservable();
    }

    dictionary(): Observable<any> {
        return this._httpClient.get("/api/v1/dictionary").pipe(
            tap((response: any) => {
                let result = this.updateDictionaryValues(response);
                this._dictionary.next(result);
            })
        );
    }

    updateDictionaryValues(dictionary: any): any {
        for (const key in dictionary) {
            if (dictionary.hasOwnProperty(key)) {
                if (typeof dictionary[key] === "object" && dictionary[key] !== null) {
                    // Llama recursivamente si el valor es un objeto.
                    this.updateDictionaryValues(dictionary[key]);
                } else if (typeof dictionary[key] === "string") {
                    // Reemplaza las ocurrencias de 'mediaSmart' por 'GeoActivate' sin cambiar el resto del contenido.
                    dictionary[key] = dictionary[key].replace(/mediaSmart/gi, "GeoActivate");
                    dictionary[key] = this._whiteLabelPerion(dictionary[key]);
                }
            }
        }
        return dictionary;
    }

    /**
     * Perion/Hivestack se vende como "DOOH Direct" (marca blanca). El diccionario del DSP trae el
     * exchange DOOH `hivestack` con la etiqueta "PERION", que se pintaba tal cual en el selector de
     * Ad Exchanges; renombrarlo aquí cubre todos los usos. El exchange NO se excluye: es real, las
     * campañas DOOH de MediaSmart pueden usarlo y se sigue guardando por su clave, que no cambia.
     *
     * Un valor que es un identificador en minúsculas (`exchanges_dooh_keys.HIVESTACK = "hivestack"`)
     * no es una etiqueta: se deja intacto para no romper a quien lo use como clave.
     */
    private _whiteLabelPerion(value: string): string {
        if (/^[a-z0-9_.:-]+$/.test(value)) {
            return value;
        }
        // Solo la palabra completa ("Experion" no se toca), sin lookbehind: Safari < 16.4 no lo soporta.
        return value.replace(/(^|[^a-z])(perion|hivestack)(?![a-z])/gi, "$1DOOH Direct");
    }

    regions(countriesList: string[]): Observable<any> {
        // Convertir el array a una cadena separada por comas
        const countriesParam = countriesList.join(",");

        // Añadir el parámetro countries a la URL
        const url = `/api/v1/dictionary/regions?countries=${countriesParam}`;

        // Hacer la petición HTTP GET
        return this._httpClient.get(url);
    }

    cities(countriesList: string[], regionsList: string[]): Observable<any> {
        // Convertir el array de países a una cadena separada por comas
        const countriesParam = countriesList?.join(",");

        // Convertir el array de regiones a una cadena con el formato "COUNTRY:REGION"
        const regionsParam = regionsList?.join(",");

        // Construir la URL con los parámetros
        const url = `/api/v1/dictionary/cities?countries=${countriesParam}&regions=${regionsParam}`;

        // Hacer la petición HTTP GET
        return this._httpClient.get(url);
    }

    countries(): Observable<any> {
        // Hacer la petición HTTP GET a la URL para obtener los países
        const url = `/api/v1/dictionary/countries`;

        return this._httpClient.get(url);
    }

    filterContextByAgent(agentValue: string): Context[] {
        const dictionary = this._dictionary.getValue();
        const items = dictionary.contexts;
        if (!items) {
            return [];
        }
        return items.filter((item) => item.agent === agentValue);
    }

    filterContextByAgentAndCategory(agentValue: string, categoryValue: string): Context[] {
        const dictionary = this._dictionary.getValue();
        const items = dictionary.contexts;
        if (!items) {
            return [];
        }
        return items.filter((item) => item.agent === agentValue && item.name.startsWith(categoryValue));
    }

    // Tranform Function from context to formFieldOption

    convertContextToFormFieldOption(context: Context): FormFieldOption {
        return {
            key: context.id,
            value: context.name,
        };
    }

    convertObjetIdNameToFormFieldOption(data: any): FormFieldOption {
        return {
            key: data.id,
            value: data.name,
        };
    }

    convertContextsToFormFieldOptions(contexts: Context[]): FormFieldOption[] {
        return contexts.map((item) => this.convertContextToFormFieldOption(item));
    }

    extractValuesObject(collection: Record<string, string>, keyasValue?: boolean): FormFieldOption[] {
        const keyList = Object.keys(collection);
        const valueList = Object.values(collection);
        const result = keyList.map((key, index) => {
            return {
                key: keyasValue ? valueList[index] : key,
                value: valueList[index],
            };
        });
        return result;
    }

    drillDownItems(collection: Record<string, string>, keyasValue?: boolean): IDrillDownItem[] {
        const keyList = Object.keys(collection);
        const valueList = Object.values(collection);
        const result = keyList.map((key, index) => {
            return {
                key: keyasValue ? valueList[index] : key,
                value: valueList[index],
                enable: false,
            };
        });
        return result;
    }

    converStringToFormFieldOptions(collection: string[]): FormFieldOption[] {
        let result: FormFieldOption[] = [];
        collection.forEach((data) => {
            let item = { key: data, value: data };
            result.push(item);
        });
        return result;
    }

    //Context converter
    // Función para capitalizar la primera letra de una cadena
    capitalizeFirstLetter(string: string): string {
        return string.charAt(0).toUpperCase() + string.slice(1);
    }

    convertContextToFormFieldOptionWithCPM(context: Context, coin?: string): FormFieldOption {
        // Formatear el CPM en EUR como un string
        let cpmValue: string;

        switch (coin) {
            case "USD":
                cpmValue = `$${context.cpm.USD} CPM`;
                break;
            case "GBP":
                cpmValue = `£${context.cpm.GBP} CPM`;
                break;
            case "EUR":
            default:
                cpmValue = `€${context.cpm.EUR} CPM`;
                break;
        }

        // Capitalizar la primera letra del agente
        const capitalizedAgent = this.capitalizeFirstLetter(context.agent);

        // Crear el valor combinado
        const value = `${capitalizedAgent} / ${context.name}  (${cpmValue})`;

        return {
            key: context.id,
            value: value,
        };
    }

    convertContextsToFormFieldOptionsWithCPM(contexts: Context[]): FormFieldOption[] {
        return contexts.map((item) => this.convertContextToFormFieldOptionWithCPM(item));
    }
    getEventFieldsOptions(): FormFieldOption[] {
        const dictionary = this._dictionary.getValue();
        const heatmapKpis = dictionary.heatmap_kpis || {};

        // Filter keys that start with "events" and map them to FormFieldOption
        const eventFieldsOptions = Object.keys(heatmapKpis)
            .filter((key) => key.startsWith("events"))
            .map((key) => ({
                key,
                value: heatmapKpis[key],
            }));

        return eventFieldsOptions;
    }

    public getOperators(countries: string): Observable<any> {
        return this._httpClient.get<any>(`/api/v1/dictionary/operators`, { params: { countries } });
    }
}
